import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Ceiling an open inline editor may never exceed, regardless of how much
 * room its scrolling ancestor actually has (spec Phase 4: "cap under 460px").
 */
const MAX_EDITOR_HEIGHT = 460;

/**
 * Walks up from `el` to the nearest ancestor that actually scrolls (computed
 * `overflow-y` of `auto`/`scroll` AND content taller than its box) — the
 * container `useEditorCap` measures room against. Returns `null` when none is
 * found, meaning the viewport itself is the only bound.
 */
function findScrollingAncestor(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const overflowY = globalThis.window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** Room between `el`'s top edge and the bottom of whatever bounds it (its scrolling ancestor, else the viewport). */
function roomBelow(el: HTMLElement): number {
  const ancestor = findScrollingAncestor(el);
  const bottom = ancestor ? ancestor.getBoundingClientRect().bottom : globalThis.window.innerHeight;
  return bottom - el.getBoundingClientRect().top;
}

export interface UseEditorCapResult {
  /** Inline `maxHeight` (px) to apply to the editor, or `undefined` when it fits uncapped. */
  maxHeight?: number;
}

/**
 * Scrolls an inline value editor into view when it opens, then caps its
 * height to whatever room the nearest scrolling ancestor leaves below it
 * (never more than `MAX_EDITOR_HEIGHT`) — without moving anything above that
 * ancestor (`scrollIntoView({ block: 'nearest' })` scrolls only as far as it
 * must, so a short host's own page never jumps).
 *
 * The order matters. Measured before the scroll, an editor opening at (or
 * below) the bottom of its scroller reads zero or negative room and would be
 * capped to nothing — invisible, with nothing left to scroll into view. So the
 * scroll comes first (on the still-uncapped editor, so `nearest` brings as
 * much of it into view as the scroller can hold) and the room is measured
 * where the editor actually ended up. A measurement that still reads no room
 * at all (a zero-height or hidden scroller) cannot describe a usable cap, so
 * the editor is left uncapped rather than collapsed.
 *
 * Measured in a layout effect so the cap applies before the first paint of
 * the open editor. `active` gates both steps: pass the editor's own
 * open/closed state so the cap is (re)computed exactly when it opens, not on
 * every render.
 */
export function useEditorCap(ref: RefObject<HTMLElement | null>, active: boolean): UseEditorCapResult {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!active) {
      setMaxHeight(undefined);
      return;
    }
    const el = ref.current;
    if (!el) return;

    el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    const room = roomBelow(el);
    setMaxHeight(room > 0 && room < MAX_EDITOR_HEIGHT ? room : undefined);
  }, [active, ref]);

  return { maxHeight };
}
